import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import { Table } from "components/common/Table";
import {
  TableColumn,
  TableRowActionsMenuItem
} from "components/common/Table/types";
import {
  describeBackupRetention,
  describeBackupSchedule
} from "modules/databases/backupSchedule";
import {
  DatabaseBackupStatus,
  TableBackupSet,
  TableDatabaseBackupFiles
} from "modules/databases/types";
import { FC, useState } from "react";
import { formatDate } from "utils/formatDate";
import { DATE_FORMATS } from "../../constants";
import * as s from "./styles";
import { BACKUP_VIEWS } from "./types";

const dateLabel = (timestamp?: number) =>
  timestamp && timestamp > 0
    ? formatDate(new Date(timestamp * 1000), DATE_FORMATS.DATETIME)
    : "—";
const setColumns: TableColumn<TableBackupSet>[] = [
  { key: "taken", label: "Taken" },
  { key: "databases", label: "Databases" },
  { key: "fileCount", label: "Files" },
  { key: "totalSize", label: "Size" },
  { key: "safety", label: "Type" }
];
const fileColumns: TableColumn<TableDatabaseBackupFiles>[] = [
  { key: "file_name", label: "Name" },
  { key: "database", label: "Database" },
  { key: "fileSize", label: "Size" },
  { key: "note", label: "Note" }
];

type ScheduleProps = {
  status: DatabaseBackupStatus | null;
  busy: boolean;
  onConfigure: () => void;
  onRemove: () => void;
};

export const BackupSchedulePanel: FC<ScheduleProps> = ({
  status,
  busy,
  onConfigure,
  onRemove
}) => (
  <s.BackupCard variant="outlined">
    <s.SectionTitle>Automatic backups</s.SectionTitle>
    <Chip
      size="small"
      label={
        !status
          ? "Loading schedule..."
          : status.cron_exist
            ? status.link_exist
              ? "Scheduled"
              : "Storage disconnected"
            : "Not scheduled"
      }
      color={
        status?.cron_exist
          ? status.link_exist
            ? "success"
            : "warning"
          : "default"
      }
    />
    <s.BackupDescription>
      {status?.cron_exist && status.cron_config
        ? describeBackupSchedule(status.cron_config, status.cron_timezone)
        : "Choose when backups run and how long they are kept."}
    </s.BackupDescription>
    {status?.cron_exist && (
      <>
        <s.InfoItem>
          <s.InfoLabel>Retention:</s.InfoLabel>
          <s.InfoValue>
            {describeBackupRetention(status.cron_retention)}
          </s.InfoValue>
        </s.InfoItem>
        {status.link_exist &&
        status.cron_next_run &&
        status.cron_next_run > 0 ? (
          <s.InfoItem>
            <s.InfoLabel>Next run (your time):</s.InfoLabel>
            <s.InfoValue>{dateLabel(status.cron_next_run)}</s.InfoValue>
          </s.InfoItem>
        ) : null}
      </>
    )}
    <s.CardActions>
      {status?.link_exist && (
        <Button variant="outlined" onClick={onConfigure} disabled={busy}>
          {status.cron_exist ? "Edit schedule" : "Set schedule"}
        </Button>
      )}
      {status?.cron_exist && (
        <Button color="error" onClick={onRemove} disabled={busy}>
          Remove schedule
        </Button>
      )}
    </s.CardActions>
  </s.BackupCard>
);

export const BackupLastRun: FC<{ status: DatabaseBackupStatus | null }> = ({
  status
}) => (
  <s.BackupCard variant="outlined">
    <s.SectionTitle>Last backup</s.SectionTitle>
    <Chip
      size="small"
      label={status?.backup_lastrun_result || "No backups yet"}
      color={
        status?.backup_lastrun_result === "Success"
          ? "success"
          : status?.backup_lastrun_result
            ? "error"
            : "default"
      }
    />
    <s.BackupDescription>
      {status?.backup_lastrun_date
        ? `Finished ${dateLabel(status.backup_lastrun_date)}`
        : "Create a backup now or set an automatic schedule."}
    </s.BackupDescription>
    {status?.backup_lastrun_message &&
      status.backup_lastrun_result === "Success" && (
        <Alert severity="info">{status.backup_lastrun_message}</Alert>
      )}
  </s.BackupCard>
);

export const DatabaseBackups: FC<
  ScheduleProps & {
    sets: TableBackupSet[] | null;
    files: TableDatabaseBackupFiles[] | null;
    setActions: TableRowActionsMenuItem<TableBackupSet>[];
    fileActions: TableRowActionsMenuItem<TableDatabaseBackupFiles>[];
    operationsDisabled: boolean;
    disabledReason: string;
    storageBusy: boolean;
    changingStorageDisabled: boolean;
    listingError: string | null;
    onCreate: () => void;
    onUndoRestore: () => void;
    onConnect: () => void;
    onDisconnect: () => void;
    onRefresh: () => void;
  }
> = (props) => {
  const { status, storageBusy, operationsDisabled, disabledReason } = props;
  const linked = Boolean(status?.link_exist);
  const [view, setView] = useState(BACKUP_VIEWS.SETS);
  const count = (rows: unknown[] | null) =>
    linked && rows ? ` (${rows.length})` : "";
  const viewToggle = (
    <ToggleButtonGroup
      size="small"
      color="primary"
      exclusive
      value={view}
      onChange={(_, next: BACKUP_VIEWS | null) => next && setView(next)}
    >
      <ToggleButton value={BACKUP_VIEWS.SETS}>
        Restore points{count(props.sets)}
      </ToggleButton>
      <ToggleButton value={BACKUP_VIEWS.FILES}>
        Files{count(props.files)}
      </ToggleButton>
    </ToggleButtonGroup>
  );
  return (
    <>
      <s.BackupHeader>
        <div>
          <s.SectionTitle>Backups</s.SectionTitle>
          <s.BackupDescription>
            Storage, automatic schedules and restore points for this database.
          </s.BackupDescription>
        </div>
        <Tooltip
          title={
            !linked
              ? "Connect backup storage first."
              : operationsDisabled
                ? disabledReason
                : ""
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              onClick={props.onCreate}
              disabled={!linked || operationsDisabled || storageBusy}
            >
              Create backup
            </Button>
          </span>
        </Tooltip>
      </s.BackupHeader>
      <s.BackupOverview>
        <s.BackupCard variant="outlined">
          <s.SectionTitle>Backup storage</s.SectionTitle>
          <Chip
            size="small"
            label={
              !status
                ? "Loading storage..."
                : linked
                  ? "Connected"
                  : "Not connected"
            }
            color={linked ? "success" : "default"}
          />
          <s.BackupDescription>
            {linked
              ? "Backups are stored in your S3 bucket."
              : "Connect an S3 bucket before creating or scheduling backups."}
          </s.BackupDescription>
          {status?.s3endpoint && (
            <s.InfoItem>
              <s.InfoLabel>Endpoint:</s.InfoLabel>
              <s.InfoValue>{status.s3endpoint}</s.InfoValue>
            </s.InfoItem>
          )}
          {status?.s3bucket && (
            <s.InfoItem>
              <s.InfoLabel>Bucket:</s.InfoLabel>
              <s.InfoValue>{status.s3bucket}</s.InfoValue>
            </s.InfoItem>
          )}
          <s.CardActions>
            <Button
              variant="outlined"
              onClick={props.onConnect}
              disabled={!status || storageBusy || props.changingStorageDisabled}
            >
              {linked ? "Change storage" : "Connect storage"}
            </Button>
            {linked && (
              <Button
                color="error"
                onClick={props.onDisconnect}
                disabled={storageBusy || props.changingStorageDisabled}
              >
                Disconnect
              </Button>
            )}
          </s.CardActions>
        </s.BackupCard>
        <BackupSchedulePanel
          status={status}
          busy={props.busy || storageBusy}
          onConfigure={props.onConfigure}
          onRemove={props.onRemove}
        />
        <BackupLastRun status={status} />
        {status?.rollback_available && (
          <s.BackupCard variant="outlined">
            <s.SectionTitle>Restore recovery</s.SectionTitle>
            <s.BackupDescription>
              A safety copy is available from before the last restore. Undoing
              the restore replaces the current data with that copy.
            </s.BackupDescription>
            {status.rollback_taken_date ? (
              <s.InfoItem>
                <s.InfoLabel>Safety copy:</s.InfoLabel>
                <s.InfoValue>
                  {dateLabel(status.rollback_taken_date)}
                </s.InfoValue>
              </s.InfoItem>
            ) : null}
            <s.CardActions>
              <Button
                variant="outlined"
                color="warning"
                onClick={props.onUndoRestore}
                disabled={!linked || operationsDisabled || storageBusy}
              >
                Undo last restore
              </Button>
            </s.CardActions>
          </s.BackupCard>
        )}
      </s.BackupOverview>
      {props.listingError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={props.onRefresh}>
              Retry
            </Button>
          }
        >
          Could not load backups: {props.listingError}
        </Alert>
      )}
      {view === BACKUP_VIEWS.SETS ? (
        <Table<TableBackupSet>
          key="backupSets"
          rows={linked ? props.sets || [] : []}
          columns={setColumns}
          actions={props.setActions}
          toolbarItems={viewToggle}
          isSearchEnabled
          isSortingEnabled
          isLoading={linked && !props.sets && !props.listingError}
        />
      ) : (
        <Table<TableDatabaseBackupFiles>
          key="backupFiles"
          rows={linked ? props.files || [] : []}
          columns={fileColumns}
          actions={props.fileActions}
          toolbarItems={viewToggle}
          isSearchEnabled
          isSortingEnabled
          isLoading={linked && !props.files && !props.listingError}
        />
      )}
    </>
  );
};
